import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { DialogCloseEvent } from '@theme/dialog';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/**
 * A custom element that adds every product referenced by its `_combo-promo-product`
 * children to the cart in a single request when its button is clicked. Products with
 * more than one variant are resolved first via the theme's shared quick-add modal
 * (the same "choose options" dialog used elsewhere), one at a time, instead of
 * silently adding whichever variant happens to be `selected_or_first_available_variant`.
 *
 * @typedef {object} ComboPromoRefs
 * @property {HTMLButtonElement} addAllButton - The "add all to cart" button.
 * @property {HTMLElement | undefined} errorMessage - Element that displays request errors.
 * @property {HTMLElement | undefined} liveRegion - Screen-reader status announcements.
 * @property {HTMLElement[] | undefined} comboItems - The product tiles rendered by `_combo-promo-product`.
 *
 * @extends Component<ComboPromoRefs>
 */
export class ComboPromoWidget extends Component {
  requiredRefs = ['addAllButton'];

  /**
   * Fetches the full cart object (not just the partial `/cart/add.js` response, which
   * for a multi-item JSON POST is just `{items: [...]}` with no `currency` key —
   * `CartLinesUpdateEvent.createCartFromAjaxResponse` needs the full cart shape to format
   * prices, and throws "Currency code is required with currency style" without it).
   * Mirrors `#refreshCart()` in `product-form.js`.
   * @returns {Promise<any>}
   */
  async #fetchCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return /** @type {any} */ (cartItemsComponent).fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /**
   * Opens the shared quick-add modal for a single product tile and waits for it to
   * close, so the customer can pick a variant before it's added. Reuses the theme's
   * existing `quick-add-component` (rendered hidden inside the tile by
   * `blocks/_combo-promo.liquid`) — that component already knows how to fetch the
   * product's variant picker, open `#quick-add-dialog`, and add the chosen variant to
   * the cart itself, so this just triggers it and observes the outcome.
   * @param {HTMLElement} tile
   * @returns {Promise<boolean>} Whether the product was actually added (false if the
   *   customer closed the dialog without adding, or no modal was available).
   */
  async #resolveVariantChoice(tile) {
    const quickAdd = /** @type {any} */ (tile.querySelector('quick-add-component'));
    const dialogComponent = /** @type {any} */ (document.getElementById('quick-add-dialog'));

    if (!quickAdd || typeof quickAdd.handleClick !== 'function' || !dialogComponent?.refs?.dialog) {
      return false;
    }

    let added = false;
    /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
    const onCartUpdate = (event) => {
      event.promise
        ?.then(({ detail }) => {
          if (!detail?.didError) added = true;
        })
        .catch(() => {});
    };
    dialogComponent.addEventListener(StandardEvents.cartLinesUpdate, onCartUpdate);

    await quickAdd.handleClick({ preventDefault() {} });

    if (!dialogComponent.refs.dialog.open) {
      // The product page fetch failed/aborted before the dialog could open.
      dialogComponent.removeEventListener(StandardEvents.cartLinesUpdate, onCartUpdate);
      return false;
    }

    await new Promise((resolve) => {
      dialogComponent.addEventListener(DialogCloseEvent.eventName, resolve, { once: true });
    });

    dialogComponent.removeEventListener(StandardEvents.cartLinesUpdate, onCartUpdate);
    return added;
  }

  /**
   * Reads the resolved variant id/availability off each rendered product tile,
   * resolves any that need a variant choice via the quick-add modal one at a time,
   * then posts the rest to the cart in a single batched request.
   * @param {Event} [event]
   */
  async addAllToCart(event) {
    event?.preventDefault?.();

    const tiles = this.refs.comboItems ?? [];
    const choiceTiles = tiles.filter((tile) => tile.dataset.needsVariantChoice === 'true');
    const simpleItems = tiles
      .filter((tile) => tile.dataset.needsVariantChoice !== 'true')
      .filter((tile) => tile.dataset.available === 'true' && tile.dataset.variantId)
      .map((tile) => ({ id: Number(tile.dataset.variantId), quantity: 1 }));

    if (choiceTiles.length === 0 && simpleItems.length === 0) return;

    const { addAllButton, errorMessage, liveRegion } = this.refs;
    addAllButton.disabled = true;
    errorMessage?.setAttribute('hidden', '');

    try {
      // Resolve one variant-choice modal at a time — each one adds its own product to
      // the cart directly (via the modal's own tested add-to-cart flow) once the
      // customer confirms a variant, or adds nothing if they close it without choosing.
      for (const tile of choiceTiles) {
        await this.#resolveVariantChoice(tile);
      }

      if (simpleItems.length > 0) {
        await this.#addSimpleItems(simpleItems);
      }

      if (liveRegion) liveRegion.textContent = Theme.translations?.added ?? 'Added to cart';
    } catch (error) {
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Add to cart failed',
          code: 'INVALID',
        })
      );

      if (errorMessage) {
        errorMessage.textContent = error?.message || 'Something went wrong. Please try again.';
        errorMessage.removeAttribute('hidden');
      }
    } finally {
      addAllButton.disabled = false;
    }
  }

  /**
   * Batch-adds every simple (single-variant, already-resolved) item in one request.
   * @param {Array<{id: number, quantity: number}>} items
   */
  async #addSimpleItems(items) {
    const deferredPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'combo-promo',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({ items }),
        })
      );
      const data = await response.json();

      if (data.status) {
        throw new Error(data.message || 'Add to cart failed');
      }

      const cart = await this.#fetchCart();

      deferredPromise.resolve(
        /** @type {any} */ ({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'combo-promo',
            sourceId: this.id,
            didError: false,
          },
        })
      );
    } catch (error) {
      deferredPromise.reject(error);
      throw error;
    }
  }
}

if (!customElements.get('combo-promo-widget')) {
  customElements.define('combo-promo-widget', ComboPromoWidget);
}
